import { useEffect, useMemo, useState } from 'react';
import { handleErrorResponse } from '../../_helpers/handleResponse.helper';
import { parseBookedDatesResponse, toIsoDateValue } from '../../_helpers/sessionPeriod.helper';
import { calendarService } from '../../_services/calendar.service';
import AppDatePicker from './datepicker';

export default function BookingDatePicker(params: {
    value: Date | string | null;
    onChange: (event: { target: { name: string; value: string | null } }) => void;
    disabledDates?: Array<Date | string>;
    disabled?: boolean; 
    label?: string;
    name?: string;
    required?: boolean;
    fullWidth?: boolean;
    withTime?: boolean;   
    emitUTC?: boolean;
    hasParent?: boolean;
    specialist?: string;
    schoolType?: string | null;
    sessionType?: string | null;
    sessionPeriod?: string | null;
    extraDisabledDates?: Array<Date | string>;
    excludeBookingId?: number | string | null;
    maxDaysAhead?: number | null;
    startTime?: string | Date;
    endTime?: string | Date;
    timeStepMinutes?: number;
    readOnly?:boolean
}) {
    const [apiDisabledDates, setApiDisabledDates] = useState<Array<string | Date>>([]);
    const [occupancyKey, setOccupancyKey] = useState<string | null>(null);

    const requestKey = [
        params.specialist ?? "",
        params.sessionType ?? "",
        params.sessionPeriod ?? "",
        params.schoolType ?? "",
        params.excludeBookingId ?? ""
    ].join("|");

    useEffect(() => {
        let cancelled = false;
        // Drop stale occupancy so a newly loaded booking date is not cleared
        // against the previous booking's disabled list.
        setApiDisabledDates([]);
        setOccupancyKey(null);

        calendarService
            .getBookedDates(params.specialist, {
                sessionType: params.sessionType,
                sessionPeriod: params.sessionPeriod,
                schoolType: params.schoolType,
                excludeBookingId: params.excludeBookingId
            })
            .then((json) => {
                if (cancelled) return;
                const parsed = parseBookedDatesResponse(json);
                setApiDisabledDates(parsed.disabledDates);
                setOccupancyKey(requestKey);
            }, (err) => {
                if (cancelled) return;
                setApiDisabledDates([]);
                setOccupancyKey(requestKey);
                handleErrorResponse(err);
            });

        return () => {
            cancelled = true;
        };
    }, [requestKey]);

    const mergedDisabled = useMemo(
        () => [...apiDisabledDates, ...(params.disabledDates ?? []), ...(params.extraDisabledDates ?? [])],
        [apiDisabledDates, params.disabledDates, params.extraDisabledDates]
    );

    useEffect(() => {
        // Only validate against occupancy that matches the current booking/slot selection.
        if (occupancyKey !== requestKey) return;
        const current = toIsoDateValue(params.value);
        if (!current) return;
        const isDisabled = mergedDisabled.some(item => toIsoDateValue(item) === current);
        if (isDisabled) {
            params.onChange({ target: { name: params.name ?? "date", value: null } });
        }
    }, [mergedDisabled, params.value, params.name, occupancyKey, requestKey]);

    const today = useMemo(() => {
        const d = new Date(); d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const yearBounds = useMemo(() => {
        const y = new Date().getFullYear();
        return {
            start: new Date(y, 0, 1, 0, 0, 0, 0),
            endDate: new Date(y, 11, 31, 0, 0, 0, 0),
            endDateTime: new Date(y, 11, 31, 23, 59, 0, 0),
        };
    }, []);

    const type = (params.schoolType ?? '').toString().toLowerCase().replace(/[\s-_]/g, '');
    const isDateOnlySchool =
        type === 'primary' ||
        type === 'specialschool' ||
        type === 'postprimary';

    const withTimeDerived =
        params.withTime === false ? false : !isDateOnlySchool;

    const minDate = useMemo(() => {
        const d = new Date(today);
        d.setDate(d.getDate() - 14);
        if (d < yearBounds.start) return yearBounds.start;
        if (d > yearBounds.endDate) return yearBounds.endDate;
        return d;
    }, [today, yearBounds]);

    const maxDate = useMemo(() => {
        let candidate: Date | undefined;
        if (params.maxDaysAhead != null) {
            candidate = new Date(today);
            candidate.setDate(candidate.getDate() + params.maxDaysAhead);
        }
        if (!candidate || candidate > yearBounds.endDate) return yearBounds.endDate;
        return candidate;
    }, [params.maxDaysAhead, today, yearBounds]);

    const minDateTime = useMemo(() => (withTimeDerived ? minDate : undefined), [withTimeDerived, minDate]);

    const maxDateTime = useMemo(() => {
        if (!withTimeDerived) return undefined;
        const endOfMaxDate = new Date(maxDate);
        endOfMaxDate.setHours(23, 59, 0, 0);
        return endOfMaxDate > yearBounds.endDateTime ? yearBounds.endDateTime : endOfMaxDate;
    }, [withTimeDerived, maxDate, yearBounds]);

    return (
        <AppDatePicker
            label={params.label}
            name={params.name}
            value={params.value}
            onChange={params.onChange}
            disabled={params.disabled} 
            disabledDates={mergedDisabled}
            minDate={minDate}
            maxDate={maxDate}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            validators={params.required ? ['required'] : []}
            errorMessages={params.required ? ['This field is required'] : []}
            fullWidth={params.fullWidth}
            withTime={withTimeDerived}
            emitUTC={params.emitUTC}
            startTime={params.startTime}
            endTime={params.endTime}
            timeStepMinutes={params.timeStepMinutes}
            readOnly={params.readOnly}
        />
    );
}
